import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { Client, RestrictedClient, UpdateClient } from '../../../api/app-types/client.app';
import { County } from '../../../api/app-types/county.app';
import { CreateContactPoint } from '../../../api/app-types/relative.app';
import { User } from '../../../api/app-types/user.app';
import { CLIENT_API } from '../../../api/injection-tokens/services.token';
import { UPLOAD_API } from '../../../api/services/upload.api';
import { CUSTOM_DATE_FORMATS } from '../../../core/adapters/custom-date-format';
import { LvrRedirectNav } from '../../../core/classes/redirect-nav.class';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { LOADING } from '../../../core/services/loading.service';
import { RECORD_MANAGER } from '../../../core/services/record.provide';
import { isContactPointComplete } from '../../../shared/helpers/is-contact-point-complete.func';
import { buildMailto, buildRequestClientMail } from '../../../shared/helpers/request-client-mail.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import {
	createErrorNotification,
	createInfoNotification,
	createSuccessNotification,
} from '../../../shared/notification/notification.factory';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { ClientResolved } from '../../../shared/routing/resolvers/client.resolver';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { ClientDetailsEdit } from '../client-details-edit/client-details-edit';
import { ClientDetailsView } from '../client-details-view/client-details-view';
import { ClientRestrictedDetailsView } from '../client-restricted-details-view/client-restricted-details-view';
import { RequestClientDialog, RequestClientDialogResult } from '../request-client-dialog/request-client-dialog';
import { normalizeDatePickerDateForUtcDisplay } from '../../../shared/helpers/date.func';
import { STATE } from '../../../core/services/state.service';
import { TENANT_FEATURES } from '../../../core/services/tenant-feature.service';
import { resolveTenantMailRecipient } from '../../../shared/helpers/tenant-mail-recipient.func';

@Component({
	selector: 'lvr-client-details',
	imports: [
		DatePipe,
		MatCardModule,
		MatIconModule,
		MatButtonModule,
		MatTooltipModule,
		TranslocoModule,
		MatNativeDateModule,
		MatDatepickerModule,
		RouterModule,
		ClientDetailsView,
		ClientDetailsEdit,
		ClientRestrictedDetailsView,
		LvrHeading,
		LvrButton,
	],
	templateUrl: './client-details.html',
	styleUrl: './client-details.scss',
})
export class ClientDetails extends LvrRedirectNav {
	protected readonly loading = inject(LOADING);
	private readonly uploadApi = inject(UPLOAD_API);
	private readonly clientApi = inject(CLIENT_API);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly session = inject(SessionManagerService);
	private readonly transloco = inject(TranslocoService);
	private readonly recordManager = inject(RECORD_MANAGER);
	private readonly dialog = inject(MatDialog);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly state = inject(STATE);
	private readonly tenantFeatures = inject(TENANT_FEATURES);
	private readonly data = toSignal(this.route.data);
	private readonly clientResolved = this.resolveDataAsSignal<ClientResolved>('client');
	private readonly user = this.resolveDataAsSignal<User>('user');
	private readonly lastTitle = signal<string | null>(null);
	public readonly isExportLoading = this.loading.isCurrentlyLoading('client-pdf.export', 'requestKey');
	public readonly canEdit = hasPermission(this.session.permissions, ['Clients.Edit', 'Clients.Edit.PhaseOneOnly']);
	public readonly isSaving = this.loading.isCurrentlyLoading('client.update', 'requestKey');

	private readonly recordTitle = computed(() => {
		const c = this.clientResolved();
		const v = c?.value as Client | RestrictedClient;
		const name = v?.name?.trim() ?? '';
		const firstName = v?.firstName?.trim() ?? '';
		if (name && firstName) return `${name}, ${firstName}`;
		if (name) return name;
		if (firstName) return firstName;
		return 'Klient';
	});

	public readonly client = computed<Client | undefined>(() => {
		const c = this.clientResolved();
		return c?.kind === 'full' ? c.value : undefined;
	});

	public readonly restrictedClient = computed<RestrictedClient | undefined>(() => {
		const c = this.clientResolved();
		return c?.kind === 'restricted' ? c.value : undefined;
	});

	public readonly editModeFromRoute = computed<boolean>(() => this.data()!['editMode']);
	public readonly counties = computed<County[]>(() => (this.data()!['counties'].entities ?? []) as County[]);
	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;

	public constructor() {
		super();
		effect(() => {
			const title = this.recordTitle();
			if (this.lastTitle() === title) return;
			// To avoid effect loop
			untracked(() => this.recordManager.updateRecordTitleOfActiveRecord(title));
			this.lastTitle.set(title);
		});
	}

	public setEditMode() {
		this.navigateAfterFinished(['edit'], { relativeTo: this.route });
	}

	/**
	 * Cancels the edit mode without saving changes.
	 */
	public handleCancel() {
		this.navigateAfterFinished(['..'], { relativeTo: this.route });
	}

	/**
	 * Saves the updated client data and exits edit mode.
	 * Shows a success or error notification depending on the result.
	 * @param formValue The updated client form data.
	 */
	public async handleSave(formValue: UpdateClient) {
		const current = this.client();
		if (!current) return;

		const body: UpdateClient = {
			...formValue,
			communicationWays: [],
		};

		const filteredCws = (formValue.communicationWays ?? []).filter(isContactPointComplete);

		if (filteredCws.length > 0) {
			const additionalCws: CreateContactPoint[] = filteredCws.map((cp) => ({
				id: cp.id === -1 ? undefined : cp.id,
				preferredWayOfContact: cp.preferredWayOfContact,
				categoryContactPointId: cp.categoryContactPointId,
				contactValue: cp.contactValue?.trim(),
				remark: cp.remark?.trim(),
			}));

			body.communicationWays!.push(...additionalCws);
		}

		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'client.update');
		try {
			await this.clientApi.update(current.id, body);
			await this.state.updateClient();
			await this.navigateAfterFinished(['..'], { relativeTo: this.route });
			this.notificationService.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.changes_saved')),
			);
		} catch {
			this.notificationService.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.changes_save_error')),
			);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	public async export(fileName: string = 'klientenstammdaten.pdf') {
		const c = this.client();
		if (!c) return;

		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'client-pdf.export');
		try {
			const blob = await this.uploadApi.getPdfOfClient(c.id);
			const downloadURL = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadURL;
			link.download = fileName;
			link.click();
			setTimeout(() => link.remove(), 200);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	public async request() {
		const result: RequestClientDialogResult = await lastValueFrom(
			this.dialog
				.open(RequestClientDialog, {
					width: '600px',
					data: {
						client: this.restrictedClient(),
					},
				})
				.afterClosed(),
		);

		if (!result) return;

		const u = this.user();
		const c = this.restrictedClient();
		const clientSearchTerm = `${c?.firstName ?? ''} ${c?.name ?? ''}`.trim();
		const assignmentType = result.assignmentType;
		const recipient = resolveTenantMailRecipient(this.tenantFeatures.tenant());
		const encodedTerm = btoa(encodeURIComponent(clientSearchTerm));
		const transferLink =
			`${window.location.origin}/desk/user/${u?.objectId}/assignments/move` +
			`?mode=client&assignmentType=${assignmentType}&term=${encodedTerm}`;
		const mail = buildRequestClientMail(
			{
				assignmentType: assignmentType,
				date: normalizeDatePickerDateForUtcDisplay(result.date),
				reason: result.reason,
				currentProfessionalName: `${u?.lastName}, ${u?.firstName}`,
				currentIfdName: `${u?.integrationServiceOffice?.remark}`,
				targetProfessionalName: `${c?.consultant?.name}, ${c?.consultant?.firstName}`,
				targetIfdName: `${c?.consultant?.integrationServiceOfficeName}`,
				transferLink: transferLink,
			},
			recipient,
		);

		this.openMailto(buildMailto(mail.recipient, mail.subject, mail.body));

		this.ntfy.addNotification(
			createInfoNotification(this.transloco.translate('client.request_client_dialog.notification_message')),
		);
	}

	private openMailto(href: string) {
		window.location.href = href;
	}
}
